import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLazyLoadQuery } from 'react-relay/hooks';

import { createStyles, Theme, Typography, withStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { TDateInterval } from 'core/AvailabilitySelector/useAvailabilitiesInitialDatesValue';
import Button from 'core/Button';
import DateRangePicker, { DateRangePickerProps } from 'core/DateRangePicker';
import ErrorBoundary from 'core/ErrorBoundary';
import { compact, get, isEqual, reduce } from 'lodash';
import moment, { Moment } from 'moment';

import { useCurrentLanguage, usePrevious } from '@lib/hooks';

import { DateRangePickerAvailabilities_Query } from './__generated__/DateRangePickerAvailabilities_Query.graphql';
import messages from './messages';

const keyFormat = 'MM/DD';

const enumerateDaysBetweenDates = (startDate: Moment, endDate: Moment) => {
  const newStartDate = startDate.clone();
  const dates = [newStartDate.format(keyFormat)];

  while (newStartDate.add(1, 'days').diff(endDate) < 0) {
    dates.push(newStartDate.format(keyFormat));
  }
  return dates;
};

const styles = ({ spacing: { unit }, palette: { primary }, breakpoints: { down } }: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',

      [down('xs')]: {
        alignItems: 'flex-start',
        flexDirection: 'column',
      },

      // Days
      '& .CalendarDay__default': {
        border: 'none',
        background: 'none',
        padding: 0,
      },
      '& .CalendarDay__blocked_out_of_range': {
        border: 'none !important',
      },
      // First/last day selected
      '& .CalendarDay__selected_span, & .CalendarDay__selected': {
        border: `1px solid ${primary.main}`,
      },
      // eslint-disable-next-line max-len
      '& .CalendarDay__default:hover:not(.CalendarDay__blocked_out_of_range) > div, & .CalendarDay__selected:active > div, & .CalendarDay__selected:hover > div': {
        fontWeight: 500,
      },
    },
    label: {
      marginRight: unit * 3,
    },
    day: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: primary.main,
    },
    legend: {
      display: 'flex',
      padding: `${unit * 1.5}px ${unit * 3}px ${unit}px`,
      alignItems: 'center',
      justifyContent: 'space-between',
      background: fade('#61AAFF', 0.8),
    },
    legendLineWrapper: {
      display: 'flex',
      flexDirection: 'column',
      width: 175,
    },
    legendLine: {
      height: unit,
      marginBottom: unit,
    },
    legendLabels: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    legendButton: {
      marginRight: -1 * unit,
    },
  });

export interface DateRangePickerAvailabilitiesProps extends DateRangePickerProps {
  className?: string;
  classes: any;
  filters: {
    appointmentType: string[] | null;
    facility: string[] | null;
    provider: string[] | null;
  };
  isRecursiveLoading?: boolean;
  onDatesChange: (value: TDateInterval) => void;
  range: TDateInterval;
}

const DateRangePickerAvailabilities: React.FC<DateRangePickerAvailabilitiesProps> = ({
  className,
  classes,
  endDate,
  filters,
  isRecursiveLoading,
  onDatesChange,
  range,
  startDate,
}) => {
  const { formatMessage } = useIntl();
  const mainColor = '#61AAFF';
  const [focusedInput, setFocusedInput] = useState<any>();
  const [fadeByDate, setFadeByDate] = useState<{ [d: string]: number }>({});
  const currentLanguage = useCurrentLanguage();

  const maxDate = moment(endDate);
  const minDate = moment(startDate);

  const isValidDateRange = useMemo(() => minDate && maxDate && minDate.isBefore(maxDate), [maxDate, minDate]);

  // parameters to graphql call to retrieve availabilities numbers
  const params = useMemo(
    () => ({
      status: 'available',
      dateMin: `${minDate.format()}`,
      dateMax: `${maxDate.format()}`,
      facility: filters.facility,
      provider: filters.provider,
      appointmentType: filters.appointmentType,
      includeNullApptTypes: true,
    }),
    [maxDate, minDate, filters],
  );
  const lastValidParams = useRef(params);

  // do not update graphql variables if dates filter is valid prevent it to trigger invalid queries
  const variables = useMemo(() => (isValidDateRange ? params : lastValidParams.current), [isValidDateRange, params]);

  const handleDatesChange = (newValue: TDateInterval) => {
    const newStartDate = moment(newValue.startDate).startOf('day');

    onDatesChange({
      startDate:
        range?.startDate && newStartDate && newStartDate.isSameOrBefore(range.startDate, 'day')
          ? range.startDate
          : newStartDate,
      endDate: moment(newValue.endDate).endOf('day'),
    });
  };

  useEffect(() => {
    // date filters can be invalid during interval selection, so, we store a copy of last valid filters
    // to use in case date filters are invalid, persisting last valid parameters and prevent graphql to trigger
    // a call with invalid filters
    if (isValidDateRange) {
      lastValidParams.current = params;
    }
  }, [isValidDateRange, params]);

  const data = useLazyLoadQuery<DateRangePickerAvailabilities_Query>(
    graphql`
      query DateRangePickerAvailabilities_Query(
        $status: EnumAvailabilityStatus
        $dateMin: String!
        $dateMax: String!
        $facility: [String!]
        $provider: [String!]
        $appointmentType: [String!]
        $includeNullApptTypes: Boolean
      ) {
        availabilitiesCountByDateArray(
          status: $status
          dateMin: $dateMin
          dateMax: $dateMax
          appointmentType: $appointmentType
          facility: $facility
          provider: $provider
          includeNullApptTypes: $includeNullApptTypes
        )
      }
    `,
    // @ts-ignore
    variables,
    { fetchPolicy: 'network-only' },
  );
  const partialRange = useMemo(() => enumerateDaysBetweenDates(minDate, maxDate), [maxDate, minDate]);
  const prevPartialRange = usePrevious(partialRange);

  useEffect(() => {
    if (!data || isEqual(partialRange, prevPartialRange)) {
      return;
    }
    const maxCount = Math.max(...compact(data.availabilitiesCountByDateArray));
    const factor = 100 / maxCount;

    const newFadeByDate = reduce(
      partialRange,
      (acc: any, key, index) => {
        const numAvailabilities = get(data.availabilitiesCountByDateArray, index) || 0;
        return {
          ...acc,
          [key]: Math.max((numAvailabilities * factor) / 100, 0.1),
        };
      },
      {},
    );

    setFadeByDate({ ...fadeByDate, ...newFadeByDate });
  }, [fadeByDate, data, partialRange, prevPartialRange]);

  // initial date filter range interval defines selectable days
  const isOutsideRange = useCallback(
    (day: Moment) => day.isBefore(range.startDate, 'day') || day.isAfter(range.endDate, 'day'),
    [range.startDate, range.endDate],
  );

  const renderDayContents = useCallback(
    (day: Moment) => {
      const style = !isOutsideRange(day)
        ? {
            backgroundColor: fade(mainColor, fadeByDate[day.format(keyFormat)] || 0.1),
          }
        : {};
      return (
        <div className={classes.day} style={style}>
          {day.format('D')}
        </div>
      );
    },
    [fadeByDate, isOutsideRange, classes, mainColor],
  );

  const renderCalendarInfo = useCallback(
    () => (
      <div className={classes.legend}>
        <div className={classes.legendLineWrapper}>
          <div
            className={classes.legendLine}
            style={{ backgroundImage: `linear-gradient(to right, white, ${mainColor})` }}
          />
          <div className={classes.legendLabels}>
            <Typography variant="caption" color="primary">
              {formatMessage(messages.fewer)}
            </Typography>
            <Typography variant="caption" color="primary">
              {formatMessage(messages.more)}
            </Typography>
          </div>
        </div>
        <Button
          className={classes.legendButton}
          size="small"
          color="primary"
          onClick={() => setFocusedInput(undefined)}
        >
          {formatMessage(messages.cancel)}
        </Button>
      </div>
    ),
    [classes, formatMessage, mainColor],
  );

  if (isRecursiveLoading) {
    return <div />;
  }

  return (
    <div className={classnames(classes.root, className)}>
      <Typography className={classes.label} variant="body1">
        {formatMessage(messages.label)}
      </Typography>
      <DateRangePicker
        startDate={startDate?.isValid() ? startDate.locale(currentLanguage) : null}
        endDate={endDate?.isValid() ? endDate.locale(currentLanguage) : null}
        focusedInput={focusedInput}
        hideKeyboardShortcutsPanel
        isOutsideRange={isOutsideRange}
        minimumNights={0}
        numberOfMonths={1}
        onDatesChange={(arg) => handleDatesChange(arg as TDateInterval)}
        onFocusChange={(value) => setFocusedInput(value)}
        renderCalendarInfo={renderCalendarInfo}
        renderDayContents={renderDayContents}
      />
    </div>
  );
};

export default withStyles(styles, { withTheme: true })((props: DateRangePickerAvailabilitiesProps) => (
  // NOTE: Normally we won't need to wrap each component with ErrorBoundary and Suspense,
  // because the parent component will use fragments in order to fetch data for all nested components.
  // In this case, the page doesn't need this data to render,
  // b/c of that, we're wrapping and fetching data for this component individually
  <ErrorBoundary>
    <Suspense fallback={null}>
      <DateRangePickerAvailabilities {...props} />
    </Suspense>
  </ErrorBoundary>
));
